import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Abschnitt } from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/entities/Abschnitt';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/entities/Mitarbeiter';
import {
  AbwesenheitsStation,
  ArbeitsbereichStation,
} from '@/domain/mitarbeiter/features/rotationsplan/features/Plaene/entities/Station';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { t } from 'i18next';

export class Rotationsplan {
  constructor({
    mitarbeiter,
    beruf,
    abschnitte,
  }: {
    mitarbeiter: Mitarbeiter;
    beruf: Beruf;
    abschnitte: Abschnitt[];
  }) {
    this.mitarbeiter = mitarbeiter;
    this.beruf = beruf;
    this.abschnitte = abschnitte;
  }

  mitarbeiter: Mitarbeiter;
  beruf: Beruf;
  abschnitte: Abschnitt[];

  getCurrentLehrjahr(): string {
    const today = dayjs();
    const currentAbschnitt = this.abschnitte.find((abschnitt) =>
      abschnitt.stationen.some(
        (station) =>
          dayjs(station.von).isBefore(today, 'day') &&
          dayjs(station.bis).isAfter(today, 'day'),
      ),
    );
    if (currentAbschnitt) {
      return currentAbschnitt.name;
    }

    const abgeschlosseneStationen = this.getAbgeschlosseneStationen();
    const letzteAbgeschlosseneStation =
      abgeschlosseneStationen.length >= 1
        ? abgeschlosseneStationen[abgeschlosseneStationen.length - 1]
        : undefined;

    if (letzteAbgeschlosseneStation) {
      const abschnitt = this.abschnitte.find(
        (abschnitt) => letzteAbgeschlosseneStation.abschnittId === abschnitt.id,
      );
      return abschnitt.name;
    }

    return t('domain.mitarbeiter.rotationsplan.rotationsplaene.geplant');
  }

  getCurrentStation(): ArbeitsbereichStation | AbwesenheitsStation | undefined {
    const today = dayjs();
    for (const abschnitt of this.abschnitte) {
      const station = abschnitt.stationen.find(
        (station) =>
          dayjs(station.von).isBefore(today, 'day') &&
          dayjs(station.bis).isAfter(today, 'day'),
      );
      if (station) {
        return station;
      }
    }
    return undefined;
  }

  getAbgeschlosseneStationen(): (
    | ArbeitsbereichStation
    | AbwesenheitsStation
  )[] {
    const today = dayjs();
    const abgeschlosseneStationen: (
      | ArbeitsbereichStation
      | AbwesenheitsStation
    )[] = [];

    for (const abschnitt of this.abschnitte) {
      for (const station of abschnitt.stationen) {
        if (dayjs(station.bis).isBefore(today, 'day')) {
          abgeschlosseneStationen.push(station);
        }
      }
    }

    return abgeschlosseneStationen;
  }

  getNextStation(): ArbeitsbereichStation | AbwesenheitsStation | undefined {
    const today = dayjs();
    let nextStation: ArbeitsbereichStation | AbwesenheitsStation | undefined;

    const stationen = this.abschnitte
      .flatMap((abschnitt) => abschnitt.stationen)
      .sort((a, b) => dayjs(a.von).valueOf() - dayjs(b.von).valueOf());

    for (const station of stationen) {
      if (dayjs(station.von).isAfter(today, 'day')) {
        nextStation = station;
        break;
      }
    }

    return nextStation;
  }

  getAmountOfStationen(): number {
    return this.abschnitte.reduce((acc, curr) => {
      return (acc += curr.stationen.length);
    }, 0);
  }
}
